<template>
  <span v-resize="checkMobile" data-testid="support-tickets-page">
    <loading
      :active.sync="isLoading"
      :is-full-page="true"
      color="#1976d2"
      :background-color="$vuetify.theme.dark ? '#000' : ''"
    ></loading>

    <v-card>
      <v-toolbar
        text
        :color="$vuetify.theme.dark ? '' : 'white'"
        v-if="!isMobile"
        class="mb-4"
      >
        <v-toolbar-title>{{ $t("support.my_tickets") }}</v-toolbar-title>

        <v-spacer></v-spacer>

        <v-text-field
          v-model="search"
          append-icon="mdi-magnify"
          :label="$t('custom.search')"
          single-line
          clearable
          hide-details
          style="max-width: 300px;"
          class="mr-4"
        ></v-text-field>

        <v-btn class="mr-2" color="primary" @click="showCreateDialog = true">
          <v-icon left>mdi-plus</v-icon>
          {{ $t("support.create_ticket") }}
        </v-btn>

        <v-btn
          icon
          @click="loadTickets"
        >
          <v-icon>mdi-refresh</v-icon>
        </v-btn>
      </v-toolbar>

      <!-- Мобильная версия тулбара -->
      <v-toolbar
        v-else
        height="auto"
        text
        :color="$vuetify.theme.dark ? '' : 'white'"
        class="mb-2"
      >
        <v-toolbar-title>{{ $t("support.my_tickets") }}</v-toolbar-title>

        <v-spacer></v-spacer>

        <v-btn icon @click="loadTickets">
          <v-icon>mdi-refresh</v-icon>
        </v-btn>
      </v-toolbar>

      <!-- Мобильный поиск -->
      <v-card-text v-if="isMobile" class="pb-0">
        <v-text-field
          v-model="search"
          append-icon="mdi-magnify"
          :label="$t('custom.search')"
          single-line
          clearable
          hide-details
        ></v-text-field>

        <div class="text-center mt-3">
          <v-btn color="primary" @click="showCreateDialog = true">
            <v-icon left>mdi-plus</v-icon>
            {{ $t("support.create_ticket") }}
          </v-btn>
        </div>
      </v-card-text>

      <!-- Список тикетов для мобильных -->
      <div v-if="isMobile">
        <v-card-text v-if="filteredTickets.length === 0" class="text-center text--secondary">
          {{ $t('support.no_tickets') }}
        </v-card-text>

        <v-list v-else>
          <v-list-item
            v-for="ticket in filteredTickets"
            :key="ticket.id"
            @click="openTicket(ticket)"
            two-line
          >
            <v-list-item-content>
              <v-list-item-title class="mb-1" :title="ticket.id">
                {{ ticket.title }}
              </v-list-item-title>
              <v-list-item-subtitle class="text--primary">
                <v-chip
                  :color="getStatusColor(ticket.status)"
                  dark
                  x-small
                  class="mr-2"
                >
                  {{ $t(`support.status_${ticket.status}`) }}
                </v-chip>
                <v-chip
                  :color="getPriorityColor(ticket.priority)"
                  dark
                  x-small
                  class="mr-2"
                >
                  {{ $t(`support.priority_${ticket.priority}`) }}
                </v-chip>
                <div v-if="ticket.rating" class="d-flex align-center mr-2">
                  <v-rating
                    :value="ticket.rating"
                    color="amber"
                    background-color="grey lighten-1"
                    readonly
                    dense
                    size="12"
                  ></v-rating>
                </div>
                <span class="text--secondary">{{ formatDate(ticket.created_at) }}</span>
              </v-list-item-subtitle>
            </v-list-item-content>

            <v-list-item-action>
              <v-icon>mdi-chevron-right</v-icon>
            </v-list-item-action>
          </v-list-item>
        </v-list>
      </div>

      <!-- Таблица тикетов для десктопа -->
      <v-data-table
        v-else
        :headers="headers"
        :items="filteredTickets"
        :loading="isLoading"
        :sort-by="['created_at']"
        :sort-desc="[true]"
        @click:row="openTicket"
        class="elevation-1 clickable-rows"
        :no-data-text="$t('support.no_tickets')"
      >
        <template v-slot:item.status="{ item }">
          <v-chip
            :color="getStatusColor(item.status)"
            dark
            small
          >
            {{ $t(`support.status_${item.status}`) }}
          </v-chip>
        </template>

        <template v-slot:item.priority="{ item }">
          <v-chip
            :color="getPriorityColor(item.priority)"
            dark
            small
          >
            {{ $t(`support.priority_${item.priority}`) }}
          </v-chip>
        </template>

        <template v-slot:item.created_at="{ item }">
          {{ formatDate(item.created_at) }}
        </template>

        <template v-slot:item.category="{ item }">
          {{ item.category ? $t(`support.category_${item.category}`) : '-' }}
        </template>

        <template v-slot:item.rating="{ item }">
          <div v-if="item.rating" class="d-flex align-center">
            <v-rating
              :value="item.rating"
              color="amber"
              background-color="grey lighten-1"
              readonly
              dense
              size="16"
            ></v-rating>
          </div>
          <span v-else class="text--secondary">-</span>
        </template>

        <template v-slot:item.actions="{ item }">
          <div class="text-center">
            <v-icon
              small
              @click.stop="openTicket(item)"
            >
              mdi-eye
            </v-icon>
          </div>
        </template>
      </v-data-table>
    </v-card>

    <!-- Диалог создания тикета -->
    <v-dialog v-model="showCreateDialog" max-width="600px" persistent>
      <v-card>
        <v-card-title>
          <span class="headline">{{ $t('support.create_ticket') }}</span>
          <v-spacer></v-spacer>
          <v-btn icon @click="closeCreateDialog">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text>
          <v-container>
            <v-row>
              <v-col cols="12">
                <v-text-field
                  v-model="newTicket.title"
                  :label="$t('support.title')"
                  counter="100"
                ></v-text-field>
              </v-col>

              <v-col cols="12">
                <v-textarea
                  v-model="newTicket.description"
                  :label="$t('support.description')"
                  rows="4"
                  counter="1000"
                ></v-textarea>
              </v-col>

              <v-col cols="12" md="6">
                <v-select
                  v-model="newTicket.priority"
                  :items="priorityOptions"
                  :label="$t('support.priority')"
                  required
                ></v-select>
              </v-col>

              <v-col cols="12" md="6">
                <v-select
                  v-model="newTicket.category"
                  :items="categoryOptions"
                  :label="$t('support.category')"
                ></v-select>
              </v-col>
            </v-row>
          </v-container>
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="closeCreateDialog">
            {{ $t('custom.close') }}
          </v-btn>
          <v-btn
            color="success"
            :loading="isCreating"
            @click="createTicket"
          >
            {{ $t('support.create_ticket') }}
          </v-btn>
          <v-spacer></v-spacer>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </span>
</template>

<script>
import moment from 'moment'
import { setMomentLocale } from '@/utils/localeInfra'

export default {
  name: 'SupportTickets',
  data() {
    return {
      isLoading: false,
      isCreating: false,
      isMobile: false,
      search: '',
      tickets: [],
      showCreateDialog: false,
      newTicket: {
        title: '',
        description: '',
        priority: 'medium',
        category: '',
      },

      headers: [
        { text: this.$t('support.title'), value: 'title', sortable: true },
        { text: this.$t('support.status'), value: 'status', sortable: true },
        { text: this.$t('support.priority'), value: 'priority', sortable: true },
        { text: this.$t('support.category'), value: 'category', sortable: true },
        { text: this.$t('support.rating'), value: 'rating', sortable: true, width: '120px' },
        { text: this.$t('support.created_at'), value: 'created_at', sortable: true },
        { text: this.$t('custom.actions'), value: 'actions', sortable: false, width: '80px' },
      ],
      priorityOptions: [
        { text: this.$t('support.priority_low'), value: 'low' },
        { text: this.$t('support.priority_medium'), value: 'medium' },
        { text: this.$t('support.priority_high'), value: 'high' },
        { text: this.$t('support.priority_urgent'), value: 'urgent' },
      ],
      categoryOptions: [
        { text: this.$t('support.category_bug'), value: 'bug' },
        { text: this.$t('support.category_booking'), value: 'booking' },
        { text: this.$t('support.category_rental_objects'), value: 'rental_objects' },
        { text: this.$t('support.category_cashbox'), value: 'cashbox' },
        { text: this.$t('support.category_calendar'), value: 'calendar' },
        { text: this.$t('support.category_document_generation'), value: 'document_generation' },
        { text: this.$t('support.category_employees'), value: 'employees' },
        { text: this.$t('support.category_tariffs'), value: 'tariffs' },
        { text: this.$t('support.category_billing'), value: 'billing' },
        { text: this.$t('support.category_integration'), value: 'integration' },
        { text: this.$t('support.category_reports'), value: 'reports' },
        { text: this.$t('support.category_performance'), value: 'performance' },
        { text: this.$t('support.category_feature_request'), value: 'feature_request' },
        { text: this.$t('support.category_authentication'), value: 'authentication' },
        { text: this.$t('support.category_other'), value: 'other' },
      ],
    }
  },
  computed: {
    filteredTickets() {
      let filtered = this.tickets

      if (this.search) {
        const searchLower = this.search.toLowerCase()
        filtered = filtered.filter(ticket =>
          ticket.title.toLowerCase().includes(searchLower) ||
          ticket.description.toLowerCase().includes(searchLower)
        )
      }

      return filtered
    },


  },
  created() {
    // Настройка локали moment
    setMomentLocale(this.$i18n.locale);

    if (this.$store.getters.isLoggedIn) {
      this.loadTickets()
      this.$store.commit('setHasNewTicket', false)
    } else {
      this.$store.commit('unsetCurrentUser')
    }
  },
  methods: {
    checkMobile() {
      this.isMobile = window.innerWidth < 768
    },

    loadTickets() {
      this.isLoading = true

      this.axios
        .get('/api/v1/support_tickets', {
          headers: {
            Authorization: this.$store.getters.getAuthToken,
          },
        })
        .then((response) => {
          this.tickets = []
          if (response.data && response.data.data) {
            response.data.data.forEach((ticket) => {
              this.tickets.push(ticket.attributes)
            })
          }
        })
        .catch((error) => {
          this.setError(error, this.$t('support.error_loading_tickets'))
          console.error(error)
        })
        .finally(() => {
          this.isLoading = false
        })
    },

    createTicket() {
      // Проверка заполненности полей
      if (!this.newTicket.title.trim()) {
        this.$notify(this.$t("support.title_required"), "", "warning");
        return
      }

      if (!this.newTicket.description.trim()) {
        this.$notify(this.$t('support.description_required'), "", "warning");
        return
      }

      if (!this.newTicket.category) {
        this.$notify(this.$t('support.category_required'), "", "warning");
        return
      }

      this.isCreating = true

      const ticketData = {
        support_ticket: {
          title: this.newTicket.title.trim(),
          description: this.newTicket.description.trim(),
          priority: this.newTicket.priority,
          category: this.newTicket.category,
          metadata: {
            browser: navigator.userAgent,
            url: window.location.href,
            timestamp: new Date().toISOString(),
          }
        }
      }

      this.axios
        .post('/api/v1/support_tickets', ticketData, {
          headers: {
            Authorization: this.$store.getters.getAuthToken,
          },
        })
        .then((response) => {
          this.$notify(this.$t('support.ticket_created_successfully'), "", "success")

          this.closeCreateDialog()
          this.loadTickets()
        })
        .catch((error) => {
          this.setError(error, this.$t('support.error_creating_ticket'))
          console.error(error)
        })
        .finally(() => {
          this.isCreating = false
        })
    },

    openTicket(ticket) {
      this.$router.push(`/support/tickets/${ticket.id}`)
    },

    closeCreateDialog() {
      this.showCreateDialog = false
      this.newTicket = {
        title: '',
        description: '',
        priority: 'medium',
        category: '',
      }

    },

    getStatusColor(status) {
      const colors = {
        open: 'blue',
        in_progress: 'orange',
        resolved: 'green',
        closed: 'grey'
      }
      return colors[status] || 'grey'
    },

    getPriorityColor(priority) {
      const colors = {
        low: 'green',
        medium: 'orange',
        high: 'red',
        urgent: 'purple'
      }
      return colors[priority] || 'grey'
    },

    formatDate(dateString) {
      return moment(dateString).format('DD.MM.YYYY HH:mm')
    },

  },
}
</script>

<style scoped>
.clickable-rows >>> tbody tr {
  cursor: pointer;
}
.clickable-rows >>> tbody tr:hover {
  background-color: rgba(0, 0, 0, 0.04);
}
</style>
